// eslint-disable-next-line
require('@webapp/common/lib/sentry').default('survey');
require('@webapp/common/lib/wdyr');

import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { v4 } from 'uuid';

import type { CustomStylesConfig } from '@webapp/common/components/custom-styles';
import { CustomStylesCtx, useViewCustomStylesNew } from '@webapp/common/components/custom-styles';
import { Loader } from '@webapp/common/components/loader';
import NotFound from '@webapp/common/components/not-found-page';
import { useForceUpdate } from '@webapp/common/hooks/use-force-update';
import { isBrowser, isPreview, SAVE_DEBOUNCE_DELAY } from '@webapp/common/lib/const';
import {
    clearSurveyTokenCookie,
    delCookieWithSubdomains,
    deleteAllCookies,
    getCookie,
    getSurveyTokenCookie,
} from '@webapp/common/lib/cookies';
import { LocalStorage } from '@webapp/common/lib/storage';
import { isEmbedded } from '@webapp/common/lib/ua';
import { extractSubdomain } from '@webapp/common/lib/utils';

import { PublicResultsRoutes } from '@webapp/public-results/src/results-routes';

import { AuthView } from '@webapp/survey/src/components/auth';
import { handleWindowMessage, WindowCmd } from '@webapp/survey/src/components/pages/embed';
import { SurveyView } from '@webapp/survey/src/components/view';
import { SurveyHeader } from '@webapp/survey/src/components/view/header';
import { RetryNotAllowedMessage } from '@webapp/survey/src/components/view/retry-not-allowed-message';
import { StatusMessage } from '@webapp/survey/src/components/view/status-message';
import { ViewWrapper } from '@webapp/survey/src/components/view/wrapper';
import { getSearchParams } from '@webapp/survey/src/machine/get-search-params';
import { isOfflineMode } from '@webapp/survey/src/machine/is-offline-mode';
import { loadIndividualTries } from '@webapp/survey/src/machine/lib';
import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { SurveyState } from '@webapp/survey/src/machine/states';
import { clearTtId, setTtId, tryToGetTtId, TT_ID } from '@webapp/survey/src/machine/tt-id';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';
import { useStore } from '@webapp/survey/src/store';

import 'iframe-resizer/js/iframeResizer.contentWindow.min';

import css from './pages.css';

if (isPreview) {
    // reset all settings on page reload
    LocalStorage.clearWithExceptions(['lang']);
    clearTtId();
}

const setMobileViewport = (): void => {
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    const vh = window.innerHeight * 0.01;
    const vw = window.innerWidth * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    document.documentElement.style.setProperty('--vw', `${vw}px`);
    document.documentElement.style.setProperty('--document-client-width', `${document.documentElement.clientWidth}px`);
};

if (isBrowser) {
    setMobileViewport();
    window.addEventListener('resize', setMobileViewport);
}

const SurveyPage: VFC = () => {
    const { send, sendApp, state } = useStore();
    const info = useStateSelector(selectInfo);
    const [pageHeight, setPageHeight] = useState();
    // const offline = isOfflineMode();
    const forceUpdate = useForceUpdate();

    const design = info.design;
    const elementColor = design.fonts.elementColor;
    const customStyles: Partial<CustomStylesConfig> = useViewCustomStylesNew(design);

    const initSurvey = useCallback(() => {
        if (!isPreview && !!getCookie('preview_opened')) {
            delCookieWithSubdomains('preview_opened');
            clearSurveyTokenCookie();
        }
        const token = getSurveyTokenCookie();
        const domain = extractSubdomain(window.location.host);
        const individualTries = loadIndividualTries(domain);

        sendApp({ type: 'INIT', domain, token, individualTries });
    }, [sendApp]);

    useEffect(() => {
        if (isBrowser && !isOfflineMode()) {
            const preventShowInstallPrompt = (event: Event) => {
                event.preventDefault();
            };
            window.addEventListener('beforeinstallprompt', preventShowInstallPrompt);
            return () => window.removeEventListener('beforeinstallprompt', preventShowInstallPrompt);
        }
    }, []);

    const updateSurvey = useCallback(
        debounce(
            SAVE_DEBOUNCE_DELAY,
            () => {
                send({ type: 'RELOAD' });
                forceUpdate();
            },
            { atBegin: false },
        ),
        [send],
    );

    useEffect(() => {
        if (isPreview || isEmbedded) {
            const handler = handleWindowMessage({
                [WindowCmd.UpdateSurvey]: updateSurvey,
                [WindowCmd.SetParentHeight]: setPageHeight,
            });
            window.addEventListener('message', handler);
            return () => window.removeEventListener('message', handler);
        }
    }, [updateSurvey]);

    useEffect(() => {
        const handleClear = ({ data }: WindowEventMap['message']) => {
            if (data === 'ttgraf-clear-response') {
                try {
                    localStorage.clear();
                    sessionStorage.clear();
                } catch {
                    // storage может быть недоступен (WebView/приватный режим) — не роняем обработчик
                }
                clearTtId();
                deleteAllCookies();
            }
        };
        window.addEventListener('message', handleClear);
        return () => {
            window.removeEventListener('message', handleClear);
        };
    }, []);

    useEffect(() => {
        if (isPreview) return;
        let timeoutId: number | null = null;
        const sp = getSearchParams();
        const ttId = tryToGetTtId();
        if (sp['id'] && sp['source'] === 'email') {
            setTtId(sp['id'] as string);
        } else if (info?.savingNotComplete) {
            if (!ttId) {
                timeoutId = window.setTimeout(() => {
                    setTtId(v4());
                }, 300);
            } else if (!sp[TT_ID]) {
                setTtId(ttId);
            }
        }
        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [info?.savingNotComplete]);

    useEffect(() => {
        initSurvey();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const notifyClick = (): void => window.parent?.postMessage('previewClick', '*');

        if (isPreview) {
            document.addEventListener('click', notifyClick);
            return () => document.removeEventListener('click', notifyClick);
        }
    }, []);

    useEffect(() => {
        if (isEmbedded && isBrowser) {
            const html = document.getElementsByTagName('html')[0];
            html.classList.add('embedded');
        }
    }, []);

    useEffect(() => {
        document.documentElement.style.setProperty('--tt-elementColor', elementColor);
    }, [elementColor]);

    useEffect(() => {
        if (isBrowser && pageHeight) {
            const html = document.getElementsByTagName('html')[0];
            const body = document.body;
            html.style.overflow = 'hidden';
            body.style.height = `${pageHeight}px`;
            body.style.overflow = 'auto';
        }
    }, [pageHeight]);

    switch (state) {
        case SurveyState.idle:
            return <Loader className={css.loader} visible={true} />;
        case SurveyState.err404:
            // case SurveyState.error:
            return <NotFound button={false} />;
        case SurveyState.auth:
        case SurveyState.authCheckPwd:
        case SurveyState.authCheckVk:
            return (
                <CustomStylesCtx.Provider value={customStyles}>
                    <AuthView />
                </CustomStylesCtx.Provider>
            );
        case SurveyState.results:
            PublicResultsRoutes.results.assign(info.shareLink ? `?linkId=${info.shareLink}` : '');
            return null;
        case SurveyState.disabled:
        case SurveyState.blocked:
            if (!info) return null;
            return (
                <CustomStylesCtx.Provider value={customStyles}>
                    <ViewWrapper>
                        <SurveyHeader className={css.surveyHeader} />
                        <StatusMessage />
                    </ViewWrapper>
                </CustomStylesCtx.Provider>
            );
        case SurveyState.retryNotAllowed:
            if (!info) return null;
            return (
                <CustomStylesCtx.Provider value={customStyles}>
                    <ViewWrapper>
                        <SurveyHeader className={css.surveyHeader} />
                        <RetryNotAllowedMessage />
                    </ViewWrapper>
                </CustomStylesCtx.Provider>
            );
        default:
            if (!info) return null;
            return (
                <CustomStylesCtx.Provider value={customStyles}>
                    <SurveyView />
                    <div id='popper' />
                    {/* {!offline && <PrivacyPopup />} */}
                </CustomStylesCtx.Provider>
            );
    }
};

export const SurveyPageWrap: FC<{
    error?: any;
}> = ({ error }) => {
    if (error) {
        return <NotFound button={false} />;
    }

    return <SurveyPage />;
};
